import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Stack,
  Title,
  Text,
  Paper,
  LoadingOverlay,
  Table,
  Group,
  TextInput,
  ActionIcon,
  Badge,
  Button
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import dayjs from 'dayjs';
import { getStatusColor } from 'constant';

const PublicAssessmentSchedulesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Extract encrypted email from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const encryptedEmail = searchParams.get('e') || '';
  const searchParam = searchParams.get('search') || '';

  // Initialize search from URL if present
  React.useEffect(() => {
    if (searchParam) {
      setSearchValue(searchParam);
    }
  }, [searchParam]);

  // Query to fetch assessment schedules with the encrypted email
  const {
    data: schedulesData,
    isLoading,
    isFetching,
    error
  } = useQuery({
    queryKey: ['publicAssessmentSchedules', encryptedEmail, searchValue, currentPage, pageSize],
    queryFn: () =>
      assessmentScheduleService.getPublicListAssessmentSchedule(encryptedEmail, {
        search: searchValue,
        page: currentPage,
        limit: pageSize
      }),
    enabled: !!encryptedEmail,
    refetchOnWindowFocus: false
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (e.target.value.length === 0) {
      navigate(`/public/assessment-schedules?e=${encryptedEmail}`);
    } else {
      navigate(`/public/assessment-schedules?e=${encryptedEmail}&search=${e.target.value}`);
    }
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    navigate(`/public/assessment-schedules?e=${encryptedEmail}`);
    setCurrentPage(1);
  };

  const handleViewDetail = (scheduleId: string) => {
    navigate(`/public/assessment-schedules/detail?e=${encryptedEmail}&id=${scheduleId}`);
  };

  // If no encrypted email is provided, show an error message
  if (!encryptedEmail) {
    return (
      <Container size='lg' py='xl'>
        <Title order={2} mb='md'>
          Assessment Schedules
        </Title>
        <Paper p='md' withBorder>
          <Text color='red'>
            No access token provided. Please use the link provided in your email.
          </Text>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size='lg' py='xl'>
      <Title order={2} mb='md'>
        My Assessment Schedules
      </Title>

      {error ? (
        <Paper p='md' withBorder>
          <Text color='red'>
            Error loading assessment schedules. Your access link may be expired or invalid.
          </Text>
        </Paper>
      ) : (
        <Stack gap='md'>
          <Group justify='space-between'>
            <TextInput
              placeholder='Search assessment schedules...'
              value={searchValue}
              onChange={handleSearch}
              style={{ width: '350px' }}
              rightSection={
                searchValue ? (
                  <ActionIcon onClick={handleClearSearch}>
                    <IconX size={16} />
                  </ActionIcon>
                ) : (
                  <IconSearch size={16} />
                )
              }
            />
          </Group>

          <Paper p='md' withBorder style={{ position: 'relative' }}>
            <LoadingOverlay visible={isLoading || isFetching} />

            {!isLoading && schedulesData?.data?.length === 0 ? (
              <Text ta={'center'} color='dimmed' py='xl'>
                No assessment schedules found.
              </Text>
            ) : (
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Assessment Name</Table.Th>
                    <Table.Th>Start Date</Table.Th>
                    <Table.Th>End Date</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {schedulesData?.data?.map((schedule: any) => {
                    const now = new Date();
                    const startTime = new Date(schedule.startTime);
                    const endTime = new Date(schedule.endTime);

                    let status = 'pending';
                    if (schedule.isDeleted) {
                      status = 'cancelled';
                    } else if (now > endTime) {
                      status = 'completed';
                    } else if (now >= startTime && now <= endTime) {
                      status = 'active';
                    }

                    return (
                      <Table.Tr key={schedule.msAssessmentScheduleID}>
                        <Table.Td>
                          <Text fw={500}>{schedule.name}</Text>
                        </Table.Td>
                        <Table.Td>{dayjs(schedule.startTime).format('DD MMM YYYY HH:mm')}</Table.Td>
                        <Table.Td>{dayjs(schedule.endTime).format('DD MMM YYYY HH:mm')}</Table.Td>
                        <Table.Td>
                          <Badge variant='subtle' bg={getStatusColor(status as any)}>
                            {status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Button
                            variant='subtle'
                            color='#7B2183'
                            onClick={() => handleViewDetail(schedule.msAssessmentScheduleID)}
                          >
                            View Details
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            )}

            {schedulesData && schedulesData.totalPages > 1 && (
              <Group justify='space-between' mt='md'>
                <Text size='sm'>
                  Page {currentPage} of {schedulesData.totalPages}
                </Text>
                <Group>
                  <Button
                    variant='outline'
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((current) => Math.max(current - 1, 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant='outline'
                    disabled={currentPage === schedulesData.totalPages}
                    onClick={() =>
                      setCurrentPage((current) => Math.min(current + 1, schedulesData.totalPages))
                    }
                  >
                    Next
                  </Button>
                </Group>
              </Group>
            )}
          </Paper>
        </Stack>
      )}
    </Container>
  );
};

export default PublicAssessmentSchedulesPage;
